import {
  type UserAudienceInfo,
  getLegacyUserAudienceBasePath,
  getLegacyUserAudiencePath,
  getUserAudiencePath,
} from "@repo/utils";

export enum Routes {
  ACCESS_DENIED = "/error/access-denied.html",
  ACCOUNTS = "/accounts/getReport/:lob=GIG",
  CLIENTS = "/accounts/clients",
  DOCUMENTS = "/accounts/documents",
  FORBIDDEN = "/error/forbidden.html",
  FORGOT_PASSWORD = "/client/forgot-password",
  FORGOT_USERNAME = "/client/forgot-username",
  GATEWAY_TIMEOUT = "/error/gateway-timeout.html",
  LOGIN = "/login",
  LOGIN_LEGACY_AUTH = "/action/login-auth",
  LOGOUT = "/logout",
  LOGOUT_LEGACY_AUTH = "/action/logout",
  NOT_FOUND = "/error/notfound.html",
  REGISTER = "/client/register/", // TODO: remove trailing slash
  SERVER_ERROR = "/error/servererror.html",
  UNAUTHORIZED = "/error/unauthorized.html",
}

/**
 * AudienceContextType defines the structure of the context that provides user audience information and audience-specific paths throughout the application.
 */
export interface AudienceContextType extends UserAudienceInfo {
  paths: {
    accessDenied: string;
    accounts: string;
    clients: string;
    documents: string;
    forbidden: string;
    forgotPassword: string;
    forgotUsername: string;
    gatewayTimeout: string;
    login: string;
    loginLegacyAuth: string;
    logout: string;
    logoutLegacyAuth: string;
    notFound: string;
    register: string;
    serverError: string;
    unauthorized: string;
  };
}

/**
 * Creates audience-specific paths for the application based on the user's audience context.
 * @param audienceInfo The user's audience information, including audience type, country, and language.
 * @returns An object containing audience-specific paths for various application routes.
 */
export const createAudiencePaths = (audienceInfo: UserAudienceInfo) => ({
  accessDenied: getUserAudiencePath(Routes.ACCESS_DENIED, audienceInfo),
  accounts: getLegacyUserAudiencePath(Routes.ACCOUNTS, audienceInfo),
  clients: getUserAudiencePath(Routes.CLIENTS, audienceInfo),
  documents: getUserAudiencePath(Routes.DOCUMENTS, audienceInfo),
  forbidden: getUserAudiencePath(Routes.FORBIDDEN, audienceInfo),
  forgotPassword: getUserAudiencePath(Routes.FORGOT_PASSWORD, audienceInfo),
  forgotUsername: getUserAudiencePath(Routes.FORGOT_USERNAME, audienceInfo),
  gatewayTimeout: getUserAudiencePath(Routes.GATEWAY_TIMEOUT, audienceInfo),
  login: getUserAudiencePath(Routes.LOGIN, audienceInfo),
  loginLegacyAuth: `${getLegacyUserAudienceBasePath(audienceInfo)}${Routes.LOGIN_LEGACY_AUTH}`,
  logout: getUserAudiencePath(Routes.LOGOUT, audienceInfo),
  logoutLegacyAuth: `${getLegacyUserAudienceBasePath(audienceInfo)}${Routes.LOGOUT_LEGACY_AUTH}`,
  notFound: getUserAudiencePath(Routes.NOT_FOUND, audienceInfo),
  register: getUserAudiencePath(Routes.REGISTER, audienceInfo),
  serverError: getUserAudiencePath(Routes.SERVER_ERROR, audienceInfo),
  unauthorized: getUserAudiencePath(Routes.UNAUTHORIZED, audienceInfo),
});
